<!-- DeleteSubscriptionForm.vue -->
<script setup>
import { ref, computed, watch } from "vue";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";

const props = defineProps({
  subscription: { type: Object, required: true },
});

const emit  = defineEmits(["close", "refresh"]);
const store = useSubscriptionStore();

const isDeleting = ref(false);

// ✅ Congela alvo e label para não mudar após refresh/seleção
const frozenSubscriptionId    = ref(null);
const frozenSubscriptionLabel = ref("");

function getSubscriptionId() {
  return props.subscription?.subscriptionId
    ?? props.subscription?.SubscriptionId
    ?? null;
}

function getSubscriptionLabel() {
  const id     = getSubscriptionId();
  const userId = props.subscription?.userId ?? props.subscription?.UserId ?? null;
  const plan   = props.subscription?.planName
    ?? props.subscription?.PlanName
    ?? (props.subscription?.planId ? `Plano #${props.subscription.planId}` : null);

  if (userId && plan) return `Usuário #${userId} — ${plan}`;
  if (id)             return `#${id}`;
  return "";
}

function freezeSnapshot() {
  frozenSubscriptionId.value    = getSubscriptionId();
  frozenSubscriptionLabel.value = getSubscriptionLabel();
}

// ✅ Mantém snapshot atualizado enquanto não está deletando
watch(
  () => [
    props.subscription?.subscriptionId,
    props.subscription?.userId,
    props.subscription?.planName,
  ],
  () => { if (!isDeleting.value) freezeSnapshot(); },
  { immediate: true }
);

const modalTitle = "Apagar Assinatura";

const messageTitle = computed(() => {
  const label = frozenSubscriptionLabel.value;
  return label
    ? `Você está prestes a apagar a assinatura "${label}".`
    : "Você está prestes a apagar esta assinatura.";
});

const messageSubtitle = computed(() => "Esta ação não pode ser desfeita.");

function buildErrorMessage(error) {
  return (
    error?.response?.data?.message
    ?? error?.message
    ?? "Ocorreu um erro ao excluir a assinatura."
  );
}

async function onConfirmDelete() {
  freezeSnapshot();
  isDeleting.value = true;

  try {
    if (!frozenSubscriptionId.value)
      throw new Error("ID da assinatura inválido para exclusão.");

    await store.deleteSubscriptionById(frozenSubscriptionId.value);
  } catch (error) {
    isDeleting.value = false;
    throw new Error(buildErrorMessage(error)); // toast vem do DeleteConfirmModal
  }
}

function handleSuccess() {
  emit("refresh");
  emit("close");
}
</script>

<template>
  <DeleteConfirmModal
    :show="true"
    :title="modalTitle"
    :messageTitle="messageTitle"
    :messageSubtitle="messageSubtitle"
    confirmText="Sim, apagar"
    cancelText="Cancelar"
    loadingText="Apagando..."
    :onConfirm="onConfirmDelete"
    @success="handleSuccess"
    @close="!isDeleting && emit('close')"
  />
</template>